<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col cols="12">
        <b-card>
          <template #header>
            <h5 class="m-0">Gerenciar Quizzes</h5>
          </template>

          <b-card-body>
            <!-- Filtros -->
            <div class="filters-section">
              <b-form-group label="Pesquisar" class="search-filter">
                <b-input-group>
                  <b-input-group-prepend>
                    <b-input-group-text>
                      <i class="fas fa-search"></i>
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="search"
                    type="search"
                    placeholder="Buscar por título..."
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button :disabled="!search" @click="search = ''"
                      >Limpar</b-button
                    >
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>

              <div v-if="canManage" class="action-button">
                <b-button size="sm" variant="primary" @click="handleAdd">
                  <i class="fa fa-plus mr-1"></i>
                  Novo Quiz
                </b-button>
              </div>
            </div>

            <!-- Tabela -->
            <div class="table-container">
              <b-table
                :items="filteredQuizzes"
                :fields="fields"
                :busy="loading"
                thead-class="courses-header"
                :tbody-tr-class="rowClass"
                borderless
                hover
                responsive
                show-empty
                :per-page="perPage"
                :current-page="currentPage"
              >
                <template #table-busy>
                  <div class="text-center py-5">
                    <b-spinner></b-spinner>
                    <span class="ml-2">Carregando...</span>
                  </div>
                </template>

                <template #empty>
                  <div class="text-muted text-center py-5">
                    <i class="fas fa-graduation-cap fa-2x"></i>
                    <p class="mt-3">Nenhum quiz encontrado</p>
                  </div>
                </template>

                <template #cell(title)="data">
                  <strong class="course-name">{{ data.value }}</strong>
                </template>

                <template #cell(questions)="data">
                  <b-badge variant="primary">
                    {{ data.value ? data.value.length : 0 }} perguntas
                  </b-badge>
                </template>

                <template #cell(passingScore)="data">
                  <span>{{ data.value }}%</span>
                </template>

                <template #cell(timeLimit)="data">
                  <span v-if="data.value">{{ formatTime(data.value) }}</span>
                  <span v-else class="text-muted">Sem limite</span>
                </template>

                <template #cell(createdAt)="data">
                  <span>{{ formatDate(data.value) }}</span>
                </template>

                <template #cell(actions)="data">
                  <b-button
                    size="sm"
                    variant="link"
                    class="action-btn"
                    @click="handleEdit(data.item)"
                    v-b-tooltip.hover
                    title="Editar"
                  >
                    <i class="fa fa-edit action-icon"></i>
                  </b-button>
                  <b-button
                    v-if="canManage"
                    size="sm"
                    variant="link"
                    class="action-btn"
                    @click="confirmDelete(data.item)"
                    v-b-tooltip.hover
                    title="Deletar"
                  >
                    <i class="fa fa-trash action-icon text-danger"></i>
                  </b-button>
                </template>
              </b-table>
            </div>

            <!-- Paginação -->
            <nav class="pagination-nav">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalQuizzes"
                :per-page="perPage"
                prev-text="Anterior"
                next-text="Próximo"
                align="center"
                class="mt-3"
              ></b-pagination>
            </nav>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Modal -->
    <b-modal
      v-model="modalVisible"
      :title="editingQuiz ? 'Editar Quiz' : 'Criar Novo Quiz'"
      size="xl"
      @ok="handleSubmit"
      :ok-title="editingQuiz ? 'Atualizar Quiz' : 'Criar Quiz'"
      cancel-title="Cancelar"
      scrollable
    >
      <b-form>
        <b-form-group label="Título do Quiz *">
          <b-form-input
            v-model="form.title"
            placeholder="Ex: Quiz de Introdução"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Descrição *">
          <b-form-textarea
            v-model="form.description"
            rows="3"
            placeholder="Digite a descrição do quiz"
            required
          ></b-form-textarea>
        </b-form-group>

        <b-row>
          <b-col md="6">
            <b-form-group label="Nota Mínima (%)">
              <b-form-input
                v-model.number="form.passingScore"
                type="number"
                min="0"
                max="100"
              ></b-form-input>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Tempo Limite (min, 0 = sem limite)">
              <b-form-input
                v-model.number="form.timeLimit"
                type="number"
                min="0"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <hr />

        <!-- Seção de Perguntas -->
        <div class="questions-section">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">Perguntas *</h5>
            <b-button size="sm" variant="success" @click="addQuestion">
              <i class="fa fa-plus mr-1"></i>Adicionar Pergunta
            </b-button>
          </div>

          <div
            v-if="form.questions.length === 0"
            class="text-center text-muted py-4"
          >
            <i class="fa fa-question-circle fa-2x"></i>
            <p class="mt-2">
              Nenhuma pergunta adicionada. Clique em "Adicionar Pergunta" para
              começar.
            </p>
          </div>

          <div
            v-for="(question, qIndex) in form.questions"
            :key="qIndex"
            class="question-card mb-3"
          >
            <div class="question-card-header">
              <h6 class="mb-0">Pergunta {{ qIndex + 1 }}</h6>
              <b-button
                size="sm"
                variant="link"
                class="text-danger p-0"
                @click="removeQuestion(qIndex)"
              >
                <i class="fa fa-times"></i>
              </b-button>
            </div>

            <b-form-group label="Texto da Pergunta *" class="mb-3">
              <b-form-input
                v-model="question.questionText"
                placeholder="Digite a pergunta"
                required
              ></b-form-input>
            </b-form-group>

            <div class="options-section">
              <div
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <label class="mb-0"><strong>Opções de Resposta</strong></label>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  @click="addOption(qIndex)"
                >
                  <i class="fa fa-plus mr-1"></i>Opção
                </b-button>
              </div>

              <div
                v-for="(option, oIndex) in question.options"
                :key="oIndex"
                class="option-row mb-2"
              >
                <b-form-radio
                  :name="`correct-${qIndex}`"
                  :checked="option.isCorrect"
                  @change="setCorrectOption(qIndex, oIndex)"
                  class="mr-2"
                ></b-form-radio>
                <b-form-input
                  v-model="option.text"
                  :placeholder="`Opção ${oIndex + 1}`"
                  required
                  class="flex-grow-1"
                ></b-form-input>
                <b-button
                  v-if="question.options.length > 2"
                  size="sm"
                  variant="link"
                  class="text-danger ml-2 p-0"
                  @click="removeOption(qIndex, oIndex)"
                >
                  <i class="fa fa-times"></i>
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { getAllQuizzes, createQuiz, updateQuiz, deleteQuiz } from "@/api/ead";

export default {
  name: "GerenciarQuizzes",
  data() {
    return {
      quizzes: [],
      loading: false,
      modalVisible: false,
      editingQuiz: null,
      search: "",
      currentPage: 1,
      perPage: 10,
      form: {
        title: "",
        description: "",
        passingScore: 70,
        timeLimit: 0,
        questions: [],
      },
      fields: [
        { key: "title", label: "Título", sortable: true },
        { key: "questions", label: "Perguntas", class: "text-center" },
        { key: "passingScore", label: "Nota Mínima", class: "text-center" },
        { key: "timeLimit", label: "Tempo", class: "text-center" },
        { key: "createdAt", label: "Criado em" },
        { key: "actions", label: "Ações", class: "text-center" },
      ],
    };
  },
  computed: {
    canManage() {
      const permissions =
        this.$store.state.user &&
        this.$store.state.user.profile &&
        this.$store.state.user.profile.permissions &&
        this.$store.state.user.profile.permissions.ead;
      const role =
        this.$store.state.user &&
        this.$store.state.user.profile &&
        this.$store.state.user.profile.role;
      return (
        (permissions && permissions.manage) ||
        role === "root" ||
        role === "company-root"
      );
    },
    filteredQuizzes() {
      if (!this.search) return this.quizzes;
      const searchLower = this.search.toLowerCase();
      return this.quizzes.filter(
        (q) => q.title && q.title.toLowerCase().includes(searchLower)
      );
    },
    totalQuizzes() {
      return this.filteredQuizzes.length;
    },
  },
  mounted() {
    this.loadQuizzes();
  },
  methods: {
    rowClass() {
      return "course-row";
    },
    async loadQuizzes() {
      this.loading = true;
      try {
        const response = await getAllQuizzes();
        this.quizzes = response.data || [];
      } catch (error) {
        console.error(error);
        this.$bvToast.toast("Erro ao carregar quizzes", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.loading = false;
      }
    },
    handleAdd() {
      this.form = {
        title: "",
        description: "",
        passingScore: 70,
        timeLimit: 0,
        questions: [],
      };
      this.editingQuiz = null;
      this.modalVisible = true;
    },
    addQuestion() {
      this.form.questions.push({
        questionText: "",
        options: [
          { text: "", isCorrect: false },
          { text: "", isCorrect: false },
        ],
      });
    },
    removeQuestion(index) {
      this.form.questions.splice(index, 1);
    },
    addOption(questionIndex) {
      this.form.questions[questionIndex].options.push({
        text: "",
        isCorrect: false,
      });
    },
    removeOption(questionIndex, optionIndex) {
      this.form.questions[questionIndex].options.splice(optionIndex, 1);
    },
    setCorrectOption(questionIndex, optionIndex) {
      this.form.questions[questionIndex].options.forEach((opt, idx) => {
        opt.isCorrect = idx === optionIndex;
      });
    },
    validateForm() {
      if (!this.form.title.trim()) {
        this.$bvToast.toast("Preencha o título do quiz", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
        return false;
      }
      if (!this.form.description.trim()) {
        this.$bvToast.toast("Preencha a descrição do quiz", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
        return false;
      }
      if (this.form.questions.length === 0) {
        this.$bvToast.toast("Adicione pelo menos uma pergunta", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
        return false;
      }
      for (let i = 0; i < this.form.questions.length; i++) {
        const question = this.form.questions[i];
        if (!question.questionText.trim()) {
          this.$bvToast.toast(`Preencha o texto da pergunta ${i + 1}`, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
          return false;
        }
        if (question.options.length < 2) {
          this.$bvToast.toast(
            `A pergunta ${i + 1} deve ter pelo menos 2 opções`,
            {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            }
          );
          return false;
        }
        const hasEmptyOption = question.options.some((opt) => !opt.text.trim());
        if (hasEmptyOption) {
          this.$bvToast.toast(
            `Todas as opções da pergunta ${i + 1} devem ser preenchidas`,
            {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            }
          );
          return false;
        }
        const correctOptions = question.options.filter((opt) => opt.isCorrect);
        if (correctOptions.length !== 1) {
          this.$bvToast.toast(
            `A pergunta ${i + 1} deve ter exatamente uma resposta correta`,
            {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            }
          );
          return false;
        }
      }
      return true;
    },
    handleEdit(quiz) {
      this.editingQuiz = quiz;
      this.form = {
        title: quiz.title,
        description: quiz.description,
        passingScore: quiz.passingScore,
        timeLimit: quiz.timeLimit ? Math.floor(quiz.timeLimit / 60) : 0,
        questions: quiz.questions.map((q) => ({
          questionText: q.questionText,
          options: q.options.map((opt) => ({
            text: opt.text,
            isCorrect: opt.isCorrect,
          })),
        })),
      };
      this.modalVisible = true;
    },
    confirmDelete(quiz) {
      this.$bvModal
        .msgBoxConfirm(`Deletar "${quiz.title}"?`, {
          title: "Confirmar",
          okVariant: "danger",
          okTitle: "Deletar",
          cancelTitle: "Cancelar",
        })
        .then((confirmed) => {
          if (confirmed) this.handleDelete(quiz._id);
        });
    },
    async handleDelete(id) {
      try {
        await deleteQuiz(id);
        this.$bvToast.toast("Quiz deletado!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
        this.loadQuizzes();
      } catch (error) {
        this.$bvToast.toast("Erro ao deletar", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      }
    },
    async handleSubmit(evt) {
      evt.preventDefault();

      if (!this.validateForm()) {
        return;
      }

      this.loading = true;
      try {
        const data = {
          ...this.form,
          timeLimit: this.form.timeLimit ? this.form.timeLimit * 60 : 0,
          questions: this.form.questions.map((q) => ({
            questionText: q.questionText,
            options: q.options.map((opt) => ({
              text: opt.text,
              isCorrect: opt.isCorrect,
            })),
            correctAnswer: q.options.find((opt) => opt.isCorrect)?.text || "",
          })),
        };

        if (this.editingQuiz) {
          await updateQuiz(this.editingQuiz._id, data);
          this.$bvToast.toast("Quiz atualizado com sucesso!", {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          });
        } else {
          await createQuiz(data);
          this.$bvToast.toast("Quiz criado com sucesso!", {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          });
        }

        this.modalVisible = false;
        this.loadQuizzes();
      } catch (error) {
        console.error("Erro ao salvar quiz:", error);
        this.$bvToast.toast(
          error.response?.data?.message || "Erro ao salvar quiz",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      } finally {
        this.loading = false;
      }
    },
    formatTime(seconds) {
      if (!seconds) return "Sem limite";
      return `${Math.floor(seconds / 60)} min`;
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString("pt-BR");
    },
  },
};
</script>

<style scoped>
.filters-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;
}

.search-filter {
  flex: 1;
}

.category-filter {
  flex-basis: 200px;
}

.action-button {
  flex-basis: 150px;
}

.filters-section .form-group {
  margin-bottom: 0;
}

.filters-section .form-control,
.filters-section .custom-select {
  border-radius: 6px;
  border: 1px solid #e9ecef;
  background-color: #fff;
  color: #495057;
  height: calc(1.5em + 1rem + 2px);
  padding: 0.5rem 1rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.filters-section .form-control:focus,
.filters-section .custom-select:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
  outline: 0;
}

.filters-section .input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-right: 0;
  border-radius: 6px 0 0 6px;
}

.filters-section .form-group label {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #495057;
  margin-bottom: 0.5rem;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.pagination-nav {
  margin-top: 1.5rem;
}

::v-deep .courses-header th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
  font-size: 0.8rem;
  text-transform: uppercase;
  border: 0;
  white-space: nowrap;
}

::v-deep .course-row {
  cursor: pointer;
  border-bottom: 1px solid #f1f3f4;
  transition: background-color 0.2s ease-in-out;
}

::v-deep .course-row:hover {
  background-color: #e9ecef !important;
}

::v-deep tbody tr:last-of-type {
  border-bottom: none;
}

::v-deep .course-row td {
  border-top: none;
  vertical-align: middle;
  padding: 0.85rem 0.75rem;
}

::v-deep .course-name {
  color: #007bff;
  font-weight: 500;
}

::v-deep .creator-text {
  color: #6c757d;
}

::v-deep .action-btn {
  padding: 0.25rem 0.5rem;
}

::v-deep .action-icon {
  color: #6c757d;
  transition: color 0.2s;
}

::v-deep .course-row:hover .action-icon {
  color: #007bff;
}

::v-deep .badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
}

::v-deep .page-item .page-link {
  border-radius: 6px;
  margin: 0 3px;
  border: 1px solid #e9ecef;
  color: #495057;
  transition: background-color 0.2s, color 0.2s;
}

::v-deep .page-item .page-link:hover {
  background-color: #f0f2f5;
  color: #0056b3;
}

::v-deep .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

::v-deep .page-item.disabled .page-link {
  background-color: transparent;
  color: #adb5bd;
}

::v-deep .card {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

::v-deep .card-header {
  border-radius: 8px 8px 0 0;
}

::v-deep .card-body {
  padding: 1.5rem;
}

/* Responsive */
@media (max-width: 768px) {
  .filters-section {
    flex-direction: column;
  }

  .search-filter,
  .category-filter,
  .action-button {
    flex-basis: auto;
    width: 100%;
  }
}

/* Estilos para perguntas no modal */
.questions-section {
  margin-top: 1rem;
}

.question-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  background-color: #f8f9fa;
}

.question-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #dee2e6;
}

.options-section {
  margin-top: 1rem;
}

.option-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.option-row input[type="text"] {
  flex: 1;
}
</style>
